import { Box, Button, Typography, alpha, styled } from "@mui/material";

import { themePalette } from "../../../theme";
import {
  CLINIC_CONTEXT_HEADER_HEIGHT,
  HEADER_HEIGHT,
} from "../components/PhlebotomyHeader.styles";

const TABLET_LAYOUT_MEDIA_QUERY = "@media (min-width: 768px)";
const DESKTOP_LAYOUT_MEDIA_QUERY = "@media (min-width: 1200px)";

const getMobileResultsSheetHeight = (
  mobileResultsSize?: "half" | "minimized",
) => {
  if (mobileResultsSize === "half") return "50%";
  return "72px";
};

interface SearchLayoutProps {
  hasResults: boolean;
  isAddressEntry: boolean;
  hasClinicContext: boolean;
  hasMobilePromo: boolean;
  mobileResultsSize?: "half" | "minimized";
}

const getSearchLayout = ({
  hasMobilePromo,
  isAddressEntry,
  mobileResultsSize,
}: SearchLayoutProps) => {
  if (isAddressEntry) {
    return `
      grid-template-rows: minmax(0, 1fr);
      grid-template-areas: "form";

      ${DESKTOP_LAYOUT_MEDIA_QUERY} {
        grid-template-columns: 440px minmax(0, 1fr);
        grid-template-areas: "form map";
      }
    `;
  }

  return `
    grid-template-rows: ${hasMobilePromo ? "auto " : ""}minmax(0, 1fr) var(--phlebotomy-mobile-sheet-height, ${getMobileResultsSheetHeight(mobileResultsSize)});
    grid-template-areas: ${hasMobilePromo ? '"promo" ' : ""}"map" "sheet";

    ${TABLET_LAYOUT_MEDIA_QUERY} {
      grid-template-columns: clamp(360px, 40vw, 440px) minmax(0, 1fr);
      grid-template-rows: auto minmax(0, 1fr);
      grid-template-areas: "controls map" "list map";
    }

    ${DESKTOP_LAYOUT_MEDIA_QUERY} {
      grid-template-columns: 440px minmax(0, 1fr);
    }
  `;
};

export const SearchLayout = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "hasResults" &&
    prop !== "isAddressEntry" &&
    prop !== "hasClinicContext" &&
    prop !== "hasMobilePromo" &&
    prop !== "mobileResultsSize",
})<SearchLayoutProps>`
  display: grid;
  position: relative;
  height: calc(
    var(--phlebotomy-visual-viewport-bottom, 100vh) -
      ${({ hasClinicContext }) =>
        hasClinicContext ? CLINIC_CONTEXT_HEADER_HEIGHT : HEADER_HEIGHT}
  );
  min-height: 0;
  overflow: hidden;
  overscroll-behavior: contain;
  flex-shrink: 0;
  grid-template-columns: minmax(0, 1fr);
  transition: grid-template-rows 280ms ease-out;

  @supports (height: 100dvh) {
    height: calc(
      var(--phlebotomy-visual-viewport-bottom, 100dvh) -
        ${({ hasClinicContext }) =>
          hasClinicContext ? CLINIC_CONTEXT_HEADER_HEIGHT : HEADER_HEIGHT}
    );
  }

  ${getSearchLayout}
`;

export const MobileResultsPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "mobileResultsSize",
})<{
  mobileResultsSize?: "half" | "minimized";
}>`
  grid-area: sheet;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-self: stretch;
  min-height: 0;
  margin: 0;
  overflow: hidden;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0;
  border-top: 1px solid ${({ theme }) => theme.palette.divider};

  ${TABLET_LAYOUT_MEDIA_QUERY} {
    display: none;
  }
`;

export const MobileResultsGrip = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 6px;
  pointer-events: none;

  &::after {
    content: "";
    display: block;
    width: 32px;
    height: 4px;
    border-radius: 999px;
    background-color: ${({ theme }) => theme.palette.custom.neutral300};
  }
`;

export const MobileResultsDragArea = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding: 4px 0 6px;
  cursor: ns-resize;
  touch-action: none;
`;

export const MobileResultsContent = styled(Box)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

export const MobileResultsList = styled(Box)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0;
  /* Let the results surface end cleanly at the browser chrome. Cards retain
   * their own separation without introducing a gray trailing gutter. */
  background-color: ${themePalette.white};
`;

export const SearchPageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "lockViewport",
})<{ lockViewport: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  ${({ lockViewport }) =>
    lockViewport &&
    `
      height: 100vh;
      height: var(--phlebotomy-visual-viewport-bottom, 100vh);
      min-height: 0;
      overflow: hidden;

      @supports (height: 100dvh) {
        height: var(--phlebotomy-visual-viewport-bottom, 100dvh);
      }
    `}
`;

export const MobileResultsBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(0.5, 1.5, 3)};
  padding-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const MobileResultsFooter = styled(Box)`
  margin-top: auto;
  width: 100%;
  background-color: ${themePalette.neutralBackground};
  padding-bottom: env(safe-area-inset-bottom);
`;

export const MobilePromoSlot = styled(Box)`
  grid-area: promo;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  /* Soft card sits here; keep chrome quiet so it isn't a banner bar. */
  padding: ${({ theme }) => theme.spacing(0.75, 1.5)};
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

/** Tablet/desktop: card above change-location in paper controls chrome. */
export const SidebarPromoSlot = styled(Box)`
  width: 100%;
  min-width: 0;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  padding-bottom: ${({ theme }) => theme.spacing(1.5)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const AddressPanel = styled(Box)`
  grid-area: form;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

export const AddressPanelContent = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(2, 1.5, 4)};

  ${TABLET_LAYOUT_MEDIA_QUERY} {
    padding: ${({ theme }) => theme.spacing(2, 3, 4)};
  }
`;

export const IntakePageBody = styled(Box)`
  display: flex;
  flex: 1;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(4, 2, 6)};
  background-color: ${({ theme }) => theme.palette.background.paper};

  ${TABLET_LAYOUT_MEDIA_QUERY} {
    padding: ${({ theme }) => theme.spacing(5, 3, 8)};
  }
`;

export const IntakePageContent = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const MobileOptionsPageShell = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background-color: ${({ theme }) => theme.palette.custom.neutral50};
`;

export const FlowPageContent = styled(AddressPanelContent)`
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  box-sizing: border-box;
`;

export const MobileOptionsTextInset = styled(Box)`
  display: flex;
  flex-direction: column;
  padding-inline: 20px;
`;

export const AddressTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  font-family: bold;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${themePalette.greyMedium};
` as typeof Typography;

export const AddressSupport = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${({ theme }) => theme.palette.text.secondary};
` as typeof Typography;

export const AddressFormFields = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const FormSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const ContactFields = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(1.5)};

  ${TABLET_LAYOUT_MEDIA_QUERY} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const OptionalLabel = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const OrDivider = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

export const UseMyLocationButton = styled(Button)`
  align-self: stretch;
  min-height: 45px;
  text-transform: uppercase;
`;

export const UseMyLocationError = styled(Typography)`
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.palette.error.main};
`;

export const AddressFormActions = styled(Box)`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

export const DoneButton = styled(Button)`
  min-width: 120px;
  height: 45px;
  text-transform: uppercase;
`;

export const IntakeFormActions = styled(AddressFormActions)`
  justify-content: center;
`;

export const IntakeSubmitButton = styled(DoneButton)`
  width: 100%;
  max-width: 280px;
  min-width: 150px;
`;

export const ResultsControls = styled(Box)`
  grid-area: controls;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  gap: 0;
  padding: 2px 16px;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-bottom: none;

  ${TABLET_LAYOUT_MEDIA_QUERY} {
    align-items: flex-start;
    padding: ${({ theme }) => theme.spacing(2, 3, 1.5)};
  }
`;

export const ChangeLocationButton = styled(Button)`
  color: ${({ theme }) => theme.palette.custom.redMedium};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  min-height: 30px;
  padding-inline: ${({ theme }) => theme.spacing(2)};
  padding-block: 2px;

  &:hover {
    background-color: ${({ theme }) =>
      alpha(theme.palette.custom.redMedium, 0.04)};
  }
`;

export const FloatingLocationButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-height: 28px;
  padding: 0 ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  & .MuiButton-startIcon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    margin: 0 ${({ theme }) => theme.spacing(1)} 0 0;
    color: ${({ theme }) => theme.palette.custom.redMedium};
  }

  & .MuiButton-startIcon + * {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const OriginDisplayLabel = styled(Typography)`
  font-size: 14px;
  line-height: 20px;
  color: ${themePalette.greyMedium};
  text-align: center;

  ${TABLET_LAYOUT_MEDIA_QUERY} {
    text-align: left;
  }
`;

export const FartherResultsNotice = styled(Typography)`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  align-self: stretch;
  margin: 0 0 ${({ theme }) => theme.spacing(1.5)};
  /* Keep flush with location cards — no centered inset that drifts on phones. */
  padding: 0;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: left;
`;

export const LoadingStatus = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const ResultsStatusContainer = styled(Box)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  min-height: 160px;
  margin-inline: auto;
  padding: ${({ theme }) => theme.spacing(2, 2, 3)};
  text-align: center;

  ${TABLET_LAYOUT_MEDIA_QUERY} {
    min-height: 200px;
    padding: ${({ theme }) => theme.spacing(3, 3, 4)};
  }

  /* Keep copy centered and readable; MUI Typography defaults can left-align. */
  & > * {
    width: 100%;
    max-width: 22rem;
    margin-inline: auto;
    text-align: center;
  }
`;
